import * as Tooltip from "@radix-ui/react-tooltip";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import NavItem from "./NavItem";
import { useSidebar } from "./SidebarContext";
import { SidebarMinimizedFlyoutPortal } from "./SidebarMinimizedFlyoutPortal";
import { logHoverMinimizedLabelProbe } from "./sidebarMinimizedLabelDebug";

/** Nav tree nodes passed into dropdown items (config-driven; aligned with NavItem usage). */
interface NavDropdownChildItem {
  url?: string;
  name?: string;
  modernizedUrl?: string;
  icon?: string;
  badge?: {
    variant: string;
    text: string;
  };
  children?: NavDropdownChildItem[];
  subMenu?: NavDropdownChildItem[];
  toggleState?: string;
}

interface NavDropdownProps {
  item: {
    name: string;
    icon?: string;
    badge?: {
      variant: string;
      text: string;
    };
    children?: NavDropdownChildItem[];
    subMenu?: NavDropdownChildItem[];
    toggleState?: string;
  };
  itemKey: string | number;
  shouldShow: boolean;
  children: NavDropdownChildItem[];
}

const NavDropdown: React.FC<NavDropdownProps> = ({
  item,
  itemKey,
  shouldShow,
  children,
}) => {
  const {
    isMinimized,
    setHoveredItemKey,
    openDropdowns,
    setOpenDropdowns,
    location,
    compactStyle,
  } = useSidebar();

  const toggleLinkRef = useRef<HTMLAnchorElement>(null);
  const [minimizedFlyoutOpen, setMinimizedFlyoutOpen] = useState(false);
  const minimizedFlyoutHideTimerRef = useRef<number | null>(null);

  const clearMinimizedFlyoutHideTimer = () => {
    if (minimizedFlyoutHideTimerRef.current !== null) {
      window.clearTimeout(minimizedFlyoutHideTimerRef.current);
      minimizedFlyoutHideTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearMinimizedFlyoutHideTimer();
  }, []);

  useEffect(() => {
    if (!isMinimized) {
      clearMinimizedFlyoutHideTimer();
      setMinimizedFlyoutOpen(false);
    }
  }, [isMinimized]);

  // Show dropdown when parent passes; allow empty children so parent stays visible
  // (e.g. easyJet when user has permission but all sub-items are filtered by feature flags)

  const toggleState = item.toggleState || `dropdown-${itemKey}`;
  const isOpen = openDropdowns[toggleState] || false;
  const currentPath =
    location?.pathname ||
    window.location.hash.replace("#", "").split("?")[0] ||
    "";
  const hasActiveChild = children.some((child: NavDropdownChildItem) => {
    const childUrl = child.url || "";
    return (
      currentPath === childUrl || currentPath.startsWith(childUrl + "/")
    );
  });
  const shouldBeOpen = isOpen || hasActiveChild;

  const hoverKey =
    typeof itemKey === "string" ? itemKey : `dropdown-${itemKey}`;

  const badge = (badgeData?: { variant: string; text: string }) => {
    if (!badgeData) return null;
    return (
      <span className={`badge badge-${badgeData.variant} ml-auto`}>
        {badgeData.text}
      </span>
    );
  };

  const handleToggle = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newState = !isOpen;
    setOpenDropdowns((prev) => ({
      ...prev,
      [toggleState]: newState,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      handleToggle(e);
    } else if (e.key === "Escape" && isOpen) {
      setOpenDropdowns((prev) => {
        const updated = { ...prev };
        delete updated[toggleState];
        return updated;
      });
    }
  };

  // When minimized, show dropdown if explicitly opened (isOpen), not just if active
  // This allows clicking to open dropdowns when minimized
  const shouldShowDropdown = isMinimized ? isOpen : shouldBeOpen;

  // For minimized sidebar, we need to render dropdown in a portal to escape overflow clipping
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const liRef = useRef<HTMLLIElement>(null);

  const dropdownRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (shouldShowDropdown && isMinimized && liRef.current) {
      // Use requestAnimationFrame to ensure DOM has updated after sidebar minimization
      requestAnimationFrame(() => {
        if (liRef.current) {
          const rect = liRef.current.getBoundingClientRect();
          // Position relative to the minimized sidebar (60px wide)
          // The dropdown should appear right next to the sidebar, not offset by the maximized width
          const minimizedSidebarWidth = 60;
          setDropdownPosition({
            top: rect.top,
            left: minimizedSidebarWidth, // Flush with sidebar edge
          });
        }
      });
    } else {
      setDropdownPosition(null);
    }
  }, [shouldShowDropdown, isMinimized, item.name]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!shouldShowDropdown) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is outside both the dropdown and its toggle button
      if (
        liRef.current &&
        dropdownRef.current &&
        !liRef.current.contains(target) &&
        !dropdownRef.current.contains(target)
      ) {
        setOpenDropdowns((prev) => {
          const updated = { ...prev };
          delete updated[toggleState];
          return updated;
        });
      }
    };

    // Add event listener with a small delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [shouldShowDropdown, toggleState, item.name]);

  const liClassName = shouldShowDropdown
    ? "nav-item nav-dropdown open"
    : "nav-item nav-dropdown";

  const dropdownContent = shouldShowDropdown ? (
    <ul
      ref={dropdownRef}
      className="nav-dropdown-items"
      data-minimized={isMinimized}
      data-compact-style={compactStyle || undefined}
      data-portal={isMinimized && dropdownPosition ? "true" : "false"}
      style={
        isMinimized && dropdownPosition
          ? {
              position: "fixed",
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              zIndex: 10002,
              minWidth: "180px",
              background: "var(--ajw-purple)",
              borderRadius: "8px",
              boxShadow:
                "0 8px 24px var(--color-black-50), 0 0 0 1px var(--ajw-blue-25)",
              border: "1px solid var(--ajw-blue-40)",
              padding: "0.375rem 0",
              margin: 0,
              overflow: "hidden",
              animation:
                "dropdownSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              backdropFilter: "blur(10px)",
            }
          : {
              animation: "dropdownFadeIn 0.2s ease-out",
            }
      }
    >
      {children.map((child, index) => (
        <NavItem
          key={child.url || index}
          item={child}
          itemKey={`${hoverKey}-${index}`}
          parentKey={hoverKey}
        />
      ))}
    </ul>
  ) : null;

  if (!shouldShow) {
    return null;
  }

  return (
    <li
      key={itemKey}
      ref={liRef}
      className={liClassName}
      data-dropdown-state={shouldShowDropdown ? "open" : "closed"}
      data-toggle-state={toggleState}
      data-is-open={isOpen}
      data-has-active-child={hasActiveChild}
      data-compact-style={compactStyle || undefined}
      data-minimized={isMinimized}
      data-sidebar-label-anchor={isMinimized ? "true" : undefined}
    >
      {/* CoreUI-style sidebar toggle: anchor + hash matches legacy markup and styles. */}
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- intentional non-navigation control */}
      <a
        ref={toggleLinkRef}
        className="nav-link nav-dropdown-toggle"
        href="#"
        onClick={(e) => {
          handleToggle(e);
        }}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-expanded={shouldShowDropdown}
        aria-haspopup="true"
        onMouseEnter={(e) => {
          clearMinimizedFlyoutHideTimer();
          if (isMinimized) {
            setMinimizedFlyoutOpen(true);
          }
          setHoveredItemKey(hoverKey);
          if (isMinimized) {
            logHoverMinimizedLabelProbe(
              e.currentTarget as HTMLElement,
              item.name,
            );
          }
        }}
        onMouseLeave={() => {
          clearMinimizedFlyoutHideTimer();
          if (isMinimized) {
            minimizedFlyoutHideTimerRef.current = window.setTimeout(() => {
              setMinimizedFlyoutOpen(false);
              minimizedFlyoutHideTimerRef.current = null;
            }, 120);
          }
          setHoveredItemKey(null);
        }}
        style={{
          color: "var(--color-white)",
          ...(compactStyle === "option3" && {
            display: "flex",
            alignItems: "center",
            margin: "0.0625rem 0.375rem",
            padding: "0.375rem 0.625rem",
            minHeight: "32px",
            fontSize: "0.75rem",
            fontWeight: 500,
            letterSpacing: "0.02em",
            lineHeight: 1.5,
            borderRadius: "4px",
            border: "none",
            background: "transparent",
            boxShadow: "none",
            position: "relative",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }),
          ...(isMinimized && { overflow: "visible" }),
        }}
        title={isMinimized ? item.name : undefined}
        data-is-open={shouldBeOpen}
        data-minimized={isMinimized}
        aria-label={isMinimized ? item.name : undefined}
        data-sidebar-label={isMinimized ? item.name : undefined}
      >
        {item.icon && (
          <i
            className={item.icon}
            aria-hidden={isMinimized ? true : undefined}
            data-minimized={isMinimized ? "true" : "false"}
            data-compact-style={compactStyle || undefined}
            data-is-open={shouldShowDropdown ? "true" : "false"}
            data-dropdown-toggle="true"
            style={
              compactStyle === "option3"
                ? {
                    width: "16px",
                    height: "16px",
                    fontSize: "0.875rem",
                    marginRight: "0.625rem",
                    background: "transparent",
                    border: "none",
                    boxShadow: "none",
                    borderRadius: 0,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--color-white)",
                    WebkitTextFillColor: "var(--color-white)",
                  }
                : {
                    color: "var(--color-white)",
                    WebkitTextFillColor: "var(--color-white)",
                  }
            }
          />
        )}
        {!isMinimized && (
          <span
            className="nav-link-text"
            style={
              compactStyle === "option3"
                ? {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }
                : undefined
            }
          >
            {item.name}
          </span>
        )}
        {badge(item.badge)}
        {!isMinimized && (
          <i
            className="fas fa-chevron-down"
            style={{
              marginLeft: "auto",
              fontSize: "0.625rem",
              transform: shouldBeOpen ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        )}
      </a>
      {isMinimized ? (
        <SidebarMinimizedFlyoutPortal
          anchorRef={toggleLinkRef}
          label={item.name}
          open={minimizedFlyoutOpen}
        />
      ) : null}
      {isMinimized && dropdownPosition
        ? (createPortal(dropdownContent, document.body) as React.ReactNode)
        : dropdownContent}
    </li>
  );
};

export default NavDropdown;
